import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Check, FileSpreadsheet, Plus, RefreshCw, Search, UserRound, X } from 'lucide-react';

import { api, type AcademicYear, type Page } from './api';
import { setMessageToast } from './toast';

type HomeroomAssignment = {
  id: string;
  level: string;
  classKey: string;
  className: string | null;
  teacher: { id: string; fullName: string };
  academicYear: { id: string; label: string };
};

type TeacherSummary = {
  id: string;
  fullName: string;
  nuptk: string | null;
  nip: string | null;
  gender: 'MALE' | 'FEMALE' | null;
  employmentStatus: string | null;
  position: string | null;
  email: string | null;
  phone: string | null;
  isActive: boolean;
  scheduleUserId: string | null;
  subjects: Array<{ subject: string }>;
  homerooms: Array<{ id: string; level: string; classKey: string; className: string | null; academicYear: { label: string } }>;
};

type ScheduleSyncStatus = 'NEW' | 'LINKED' | 'UNCHANGED' | 'SKIPPED';

type ScheduleSyncPreview = {
  configured: boolean;
  items: Array<{ scheduleUserId: string; fullName: string; email: string; roles: string[]; status: ScheduleSyncStatus; reason?: string }>;
  counts: { total: number; new: number; linked: number; unchanged: number; skipped: number };
};

type TeacherDetail = TeacherSummary & {
  nik: string | null;
  birthPlace: string | null;
  birthDate: string | null;
  religion: string | null;
  address: Record<string, string | null> | null;
  highestEducation: string | null;
  fieldOfStudy: string | null;
  certification: string | null;
  startedOn: string | null;
  notes: string | null;
  homerooms: HomeroomAssignment[];
};

type ImportPreview = {
  batch: { id: string; status: string; rowCount: number; quarantinedCount: number; adapter: string; fileName: string };
  preview: Array<{ sourceRowNo: number; outcome: string; reasonCodes: string[] }>;
  warningCodes: string[];
  duplicate?: boolean;
};

type TeacherForm = {
  id: string | null;
  fullName: string;
  nuptk: string;
  nip: string;
  nik: string;
  gender: string;
  birthPlace: string;
  birthDate: string;
  religion: string;
  email: string;
  phone: string;
  employmentStatus: string;
  position: string;
  highestEducation: string;
  fieldOfStudy: string;
  certification: string;
  startedOn: string;
  isActive: boolean;
  notes: string;
  subjects: string;
  street: string;
  village: string;
  district: string;
  regency: string;
  province: string;
};

const employmentChoices = ['PNS', 'PPPK', 'GTY', 'GTT', 'HONORER'];
const employmentLabels: Record<string, string> = {
  PNS: 'PNS',
  PPPK: 'PPPK',
  GTY: 'Guru tetap yayasan',
  GTT: 'Guru tidak tetap',
  HONORER: 'Honorer',
};
const levelChoices = ['KB', 'SD', 'SMP', 'SMA'];

const emptyForm: TeacherForm = {
  id: null,
  fullName: '', nuptk: '', nip: '', nik: '', gender: '', birthPlace: '', birthDate: '', religion: '',
  email: '', phone: '', employmentStatus: '', position: '', highestEducation: '', fieldOfStudy: '',
  certification: '', startedOn: '', isActive: true, notes: '', subjects: '',
  street: '', village: '', district: '', regency: '', province: '',
};

function toForm(detail: TeacherDetail): TeacherForm {
  const address = detail.address ?? {};
  return {
    id: detail.id,
    fullName: detail.fullName,
    nuptk: detail.nuptk ?? '',
    nip: detail.nip ?? '',
    nik: detail.nik ?? '',
    gender: detail.gender ?? '',
    birthPlace: detail.birthPlace ?? '',
    birthDate: detail.birthDate ? detail.birthDate.slice(0, 10) : '',
    religion: detail.religion ?? '',
    email: detail.email ?? '',
    phone: detail.phone ?? '',
    employmentStatus: detail.employmentStatus ?? '',
    position: detail.position ?? '',
    highestEducation: detail.highestEducation ?? '',
    fieldOfStudy: detail.fieldOfStudy ?? '',
    certification: detail.certification ?? '',
    startedOn: detail.startedOn ? detail.startedOn.slice(0, 10) : '',
    isActive: detail.isActive,
    notes: detail.notes ?? '',
    subjects: detail.subjects.map((item) => item.subject).join(', '),
    street: address['street'] ?? '',
    village: address['village'] ?? '',
    district: address['district'] ?? '',
    regency: address['regency'] ?? '',
    province: address['province'] ?? '',
  };
}

function Field({ label, value, onChange, type = 'text', required, hint, placeholder }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
  required?: boolean;
  hint?: string;
  placeholder?: string;
}) {
  return (
    <label className="field">
      <span>{label}{required && <i> *</i>}</span>
      <input type={type} value={value} onChange={(event) => onChange(event.target.value)} required={required} placeholder={placeholder} maxLength={200} />
      {hint && <small>{hint}</small>}
    </label>
  );
}

function ChoiceField({ label, value, onChange, choices, labels }: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  choices: string[];
  labels?: Record<string, string>;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value)}>
        <option value="">Belum diisi</option>
        {choices.map((choice) => <option key={choice} value={choice}>{labels?.[choice] ?? choice}</option>)}
      </select>
    </label>
  );
}

export function TeachersPanel({ years }: { years: AcademicYear[] }) {
  const [teachers, setTeachers] = useState<TeacherSummary[]>([]);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<'active' | 'inactive' | 'all'>('active');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState<TeacherForm | null>(null);
  const [homerooms, setHomerooms] = useState<HomeroomAssignment[]>([]);
  const [homeroomYear, setHomeroomYear] = useState('');
  const [homeroomDraft, setHomeroomDraft] = useState({ teacherId: '', level: 'SD', className: '' });
  const [importOpen, setImportOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [syncOpen, setSyncOpen] = useState(false);
  const [syncPreview, setSyncPreview] = useState<ScheduleSyncPreview | null>(null);

  const loadTeachers = async (search = query, status = filter) => {
    try {
      const result = await api<Page<TeacherSummary>>(
        `/teachers?q=${encodeURIComponent(search)}&status=${status}`,
      );
      setTeachers(result.items);
      setError('');
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Daftar guru gagal dimuat.');
    }
  };

  const loadHomerooms = async (academicYearId: string) => {
    if (!academicYearId) return;
    try {
      const result = await api<{ items: HomeroomAssignment[] }>(`/teachers/homerooms?academicYearId=${academicYearId}`);
      setHomerooms(result.items);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Data wali kelas gagal dimuat.');
    }
  };

  useEffect(() => { void loadTeachers(query, filter); }, [filter]);
  useEffect(() => {
    const timer = window.setTimeout(() => { void loadTeachers(query, filter); }, 250);
    return () => window.clearTimeout(timer);
  }, [query]);
  useEffect(() => {
    if (!homeroomYear && years[0]) setHomeroomYear(years[0].id);
  }, [years, homeroomYear]);
  useEffect(() => { if (homeroomYear) void loadHomerooms(homeroomYear); }, [homeroomYear]);

  const openEditor = async (teacherId: string) => {
    setBusy(true);
    setError('');
    try {
      const { item } = await api<{ item: TeacherDetail }>(`/teachers/${teacherId}`);
      setForm(toForm(item));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Data guru gagal dimuat.');
    } finally {
      setBusy(false);
    }
  };

  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form) return;
    setBusy(true);
    setError('');
    const address = {
      street: form.street, village: form.village, district: form.district,
      regency: form.regency, province: form.province,
    };
    const payload = {
      fullName: form.fullName.trim(),
      nuptk: form.nuptk,
      nip: form.nip,
      nik: form.nik,
      gender: form.gender || null,
      birthPlace: form.birthPlace,
      birthDate: form.birthDate || null,
      religion: form.religion,
      email: form.email,
      phone: form.phone,
      employmentStatus: form.employmentStatus || null,
      position: form.position,
      highestEducation: form.highestEducation,
      fieldOfStudy: form.fieldOfStudy,
      certification: form.certification,
      startedOn: form.startedOn || null,
      isActive: form.isActive,
      notes: form.notes,
      address,
      subjects: form.subjects.split(',').map((item) => item.trim()).filter(Boolean),
    };
    try {
      await api(form.id ? `/teachers/${form.id}` : '/teachers', {
        method: form.id ? 'PATCH' : 'POST',
        body: JSON.stringify(payload),
      });
      setForm(null);
      setMessageToast(form.id ? 'Data guru diperbarui.' : 'Guru baru ditambahkan.');
      await loadTeachers();
      if (homeroomYear) await loadHomerooms(homeroomYear);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Data guru tidak dapat disimpan.');
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (teacher: TeacherSummary) => {
    setBusy(true);
    setError('');
    try {
      await api(`/teachers/${teacher.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ isActive: !teacher.isActive }),
      });
      setMessageToast(teacher.isActive ? 'Guru dinonaktifkan.' : 'Guru diaktifkan kembali.');
      await loadTeachers();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Status guru tidak dapat diubah.');
    } finally {
      setBusy(false);
    }
  };

  const removeTeacher = async (teacher: TeacherSummary) => {
    if (!window.confirm(`Hapus data ${teacher.fullName}? Tindakan ini tercatat di audit dan tidak dapat dibatalkan.`)) return;
    setBusy(true);
    setError('');
    try {
      await api(`/teachers/${teacher.id}`, { method: 'DELETE' });
      setMessageToast('Data guru dihapus.');
      await loadTeachers();
      if (homeroomYear) await loadHomerooms(homeroomYear);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Data guru tidak dapat dihapus.');
    } finally {
      setBusy(false);
    }
  };

  const assignHomeroom = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      await api('/teachers/homerooms', {
        method: 'POST',
        body: JSON.stringify({ ...homeroomDraft, academicYearId: homeroomYear }),
      });
      setHomeroomDraft({ teacherId: '', level: homeroomDraft.level, className: '' });
      setMessageToast('Wali kelas ditetapkan.');
      await loadHomerooms(homeroomYear);
      await loadTeachers();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Wali kelas tidak dapat ditetapkan.');
    } finally {
      setBusy(false);
    }
  };

  const removeHomeroom = async (homeroom: HomeroomAssignment) => {
    setBusy(true);
    setError('');
    try {
      await api(`/teachers/homerooms/${homeroom.id}`, { method: 'DELETE' });
      await loadHomerooms(homeroomYear);
      await loadTeachers();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Penetapan wali kelas tidak dapat dihapus.');
    } finally {
      setBusy(false);
    }
  };

  const loadScheduleSync = async () => {
    setBusy(true);
    setError('');
    try {
      setSyncPreview(await api<ScheduleSyncPreview>('/teachers/sync/schedule'));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Direktori jadwal tidak dapat dibaca.');
    } finally {
      setBusy(false);
    }
  };

  const openScheduleSync = () => {
    setSyncOpen(true);
    setSyncPreview(null);
    void loadScheduleSync();
  };

  const applyScheduleSync = async () => {
    setBusy(true);
    setError('');
    try {
      const result = await api<{ created: number; linked: number; unchanged: number; skipped: number }>(
        '/teachers/sync/schedule',
        { method: 'POST' },
      );
      setMessageToast(`Sinkronisasi jadwal: ${result.created} guru baru, ${result.linked} ditautkan, ${result.unchanged} sudah ada.`);
      setSyncOpen(false);
      setSyncPreview(null);
      await loadTeachers();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Sinkronisasi jadwal tidak berhasil.');
    } finally {
      setBusy(false);
    }
  };

  const validateImport = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!file) {
      setError('Pilih berkas spreadsheet terlebih dahulu.');
      return;
    }
    const body = new FormData();
    body.append('file', file);
    setBusy(true);
    setError('');
    try {
      const result = await api<ImportPreview>('/teachers/import/validate', { method: 'POST', body });
      setPreview(result);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Berkas tidak dapat diperiksa.');
    } finally {
      setBusy(false);
    }
  };

  const commitImport = async () => {
    if (!preview) return;
    setBusy(true);
    setError('');
    try {
      const result = await api<{ createdCount: number; updatedCount: number; unchangedCount: number }>(
        `/teachers/import/${preview.batch.id}/commit`,
        { method: 'POST' },
      );
      setPreview(null);
      setFile(null);
      setMessageToast(`Impor guru selesai: ${result.createdCount} baru, ${result.updatedCount} diperbarui, ${result.unchangedCount} tetap.`);
      await loadTeachers();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Impor guru tidak berhasil.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {error && <div className="alert" role="alert">{error}</div>}
      <div className="page-title">
        <div>
          <p className="eyebrow">DATABASE SEKOLAH</p>
          <h1>Guru dan tenaga pendidik</h1>
          <p>Data pendidik, status kepegawaian, mata pelajaran, dan wali kelas.</p>
        </div>
        <div className="teacher-actions">
          <button className="button secondary" onClick={openScheduleSync}>
            <RefreshCw size={16} /> Tarik dari jadwal
          </button>
          <button className="button secondary" onClick={() => { setImportOpen((open) => !open); setPreview(null); }}>
            <FileSpreadsheet size={16} /> Impor guru
          </button>
          <button className="button primary" onClick={() => setForm({ ...emptyForm })}>
            <Plus size={16} /> Tambah guru
          </button>
        </div>
      </div>

      <div className="toolbar">
        <div className="searchbox">
          <Search size={17} />
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Cari nama, NUPTK, NIP, NIK, atau mapel…" />
        </div>
        <span className="result-count">{teachers.length} guru ditampilkan</span>
      </div>
      <div className="status-tabs">
        {(['active', 'inactive', 'all'] as const).map((value) => (
          <button key={value} className={filter === value ? 'selected' : ''} onClick={() => setFilter(value)}>
            {value === 'active' ? 'Aktif' : value === 'inactive' ? 'Nonaktif' : 'Semua'}
          </button>
        ))}
      </div>

      <div className="table-card">
        <table>
          <thead>
            <tr><th>Nama guru</th><th>Mata pelajaran</th><th>Kepegawaian</th><th>Wali kelas</th><th>Kontak</th><th /></tr>
          </thead>
          <tbody>
            {teachers.map((teacher) => {
              const homeroom = teacher.homerooms[0];
              return (
                <tr key={teacher.id}>
                  <td>
                    <strong>{teacher.fullName}</strong>
                    <small>
                      NUPTK {teacher.nuptk || '—'} · NIP {teacher.nip || '—'}
                      {teacher.scheduleUserId && ' · Dari jadwal'}
                      {!teacher.isActive && ' · Nonaktif'}
                    </small>
                  </td>
                  <td>
                    {teacher.subjects.length
                      ? <div className="chip-row">{teacher.subjects.map((item) => <span className="chip" key={item.subject}>{item.subject}</span>)}</div>
                      : <span>Belum diisi</span>}
                  </td>
                  <td>
                    {teacher.employmentStatus ? employmentLabels[teacher.employmentStatus] ?? teacher.employmentStatus : '—'}
                    {teacher.position && <small>{teacher.position}</small>}
                  </td>
                  <td>
                    {homeroom ? `${homeroom.className ?? homeroom.classKey} · ${homeroom.academicYear.label}` : '—'}
                  </td>
                  <td>
                    {teacher.phone || teacher.email || '—'}
                    {teacher.phone && teacher.email && <small>{teacher.email}</small>}
                  </td>
                  <td>
                    <div className="teacher-actions">
                      <button className="button secondary small" disabled={busy} onClick={() => void openEditor(teacher.id)}>Ubah</button>
                      <button className="button secondary small" disabled={busy} onClick={() => void toggleActive(teacher)}>
                        {teacher.isActive ? 'Nonaktifkan' : 'Aktifkan'}
                      </button>
                      <button className="button danger small" disabled={busy} onClick={() => void removeTeacher(teacher)}>Hapus</button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {teachers.length === 0 && <tr><td colSpan={6} className="empty-state">Belum ada data guru untuk ditampilkan.</td></tr>}
          </tbody>
        </table>
      </div>

      {importOpen && (
        <section className="teacher-import">
          <div className="section-title">Impor data guru</div>
          <p className="result-count">
            Unggah ekspor GTK atau daftar guru. Kolom dicocokkan berdasarkan nama header, bukan urutan.
            Berkas diperiksa dulu; belum ada data yang berubah sebelum Anda menekan Impor.
          </p>
          <div className="teacher-import-grid">
            <form className="import-card" onSubmit={(event) => void validateImport(event)}>
              <label className="file-drop">
                <FileSpreadsheet size={22} />
                <strong>{file?.name || 'Pilih berkas spreadsheet'}</strong>
                <small>Excel .xlsx atau SpreadsheetML .xls · maksimal 8 MB</small>
                <input
                  type="file"
                  accept=".xlsx,.xls,.xlsm"
                  onChange={(event) => { setFile(event.target.files?.[0] ?? null); setPreview(null); }}
                />
              </label>
              <button className="button primary full" type="submit" disabled={busy || !file}>Periksa berkas</button>
            </form>
            {preview && (
              <div className="preview-card">
                <div className="preview-title">
                  <div>
                    <p className="eyebrow">{preview.duplicate ? 'BERKAS SUDAH DIUNGGAH' : 'PRATINJAU'}</p>
                    <h2>{preview.batch.rowCount} baris</h2>
                    <p>{preview.batch.quarantinedCount} baris perlu perbaikan sebelum dapat diimpor.</p>
                  </div>
                </div>
                {preview.warningCodes.length > 0 && (
                  <div className="warning-line">Peringatan: {preview.warningCodes.join(', ')}</div>
                )}
                <div className="preview-actions">
                  <button className="button primary" disabled={busy || preview.duplicate} onClick={() => void commitImport()}>
                    <Check size={15} /> Impor sekarang
                  </button>
                </div>
                <div className="preview-table-wrap">
                  <table className="preview-table">
                    <thead><tr><th>Baris</th><th>Hasil</th><th>Catatan</th></tr></thead>
                    <tbody>
                      {preview.preview.map((row) => (
                        <tr key={row.sourceRowNo}>
                          <td>{row.sourceRowNo}</td>
                          <td>{row.outcome}</td>
                          <td>{row.reasonCodes.join(', ') || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {syncOpen && (
        <section className="teacher-import">
          <div className="section-title"><RefreshCw size={15} /> Tarik data guru dari jadwal</div>
          <p className="result-count">
            Nama dan email staf diambil dari aplikasi jadwal melalui token layanan. Guru yang sudah ada
            tidak diubah; pencocokan memakai akun jadwal lalu email, sehingga aman dijalankan berulang.
          </p>
          {!syncPreview && <div className="loading-inline">Membaca direktori jadwal…</div>}
          {syncPreview && !syncPreview.configured && (
            <div className="warning-line">
              Sinkronisasi belum diatur: set <code>SCHEDULE_SERVICE_TOKEN</code> pada API data peserta didik.
            </div>
          )}
          {syncPreview && syncPreview.configured && (
            <>
              <div className="sync-summary">
                <span className="chip">{syncPreview.counts.total} staf di jadwal</span>
                <span className="chip">{syncPreview.counts.new} guru baru</span>
                <span className="chip">{syncPreview.counts.linked} ditautkan</span>
                <span className="chip">{syncPreview.counts.unchanged} sudah ada</span>
                {syncPreview.counts.skipped > 0 && <span className="chip muted">{syncPreview.counts.skipped} dilewati</span>}
              </div>
              <div className="preview-table-wrap">
                <table className="preview-table">
                  <thead><tr><th>Nama</th><th>Email</th><th>Hasil</th></tr></thead>
                  <tbody>
                    {syncPreview.items.map((item) => (
                      <tr key={item.scheduleUserId}>
                        <td>{item.fullName}</td>
                        <td>{item.email}</td>
                        <td>
                          {item.status === 'NEW' ? 'Guru baru'
                            : item.status === 'LINKED' ? 'Ditautkan ke guru yang ada'
                              : item.status === 'UNCHANGED' ? 'Sudah ada'
                                : item.reason === 'EMAIL_LINKED_TO_ANOTHER_USER' ? 'Dilewati: email milik akun jadwal lain' : 'Dilewati'}
                        </td>
                      </tr>
                    ))}
                    {syncPreview.items.length === 0 && <tr><td colSpan={3} className="empty-state">Direktori jadwal kosong.</td></tr>}
                  </tbody>
                </table>
              </div>
              <div className="preview-actions">
                <button type="button" className="button secondary" onClick={() => setSyncOpen(false)} disabled={busy}>Tutup</button>
                <button
                  type="button"
                  className="button primary"
                  disabled={busy || syncPreview.counts.new + syncPreview.counts.linked === 0}
                  onClick={() => void applyScheduleSync()}
                >
                  <Check size={15} /> Tambahkan {syncPreview.counts.new} guru
                </button>
              </div>
            </>
          )}
        </section>
      )}

      <section className="homeroom-card">
        <div className="section-title"><UserRound size={15} /> Wali kelas</div>
        <p className="result-count">Satu guru per kelas per tahun ajaran. Memilih guru lain pada kelas yang sama akan menggantikan wali kelas sebelumnya.</p>
        <form className="homeroom-form" onSubmit={(event) => void assignHomeroom(event)}>
          <label className="field">
            <span>Tahun ajaran</span>
            <select value={homeroomYear} onChange={(event) => setHomeroomYear(event.target.value)} required>
              <option value="">Pilih tahun ajaran</option>
              {years.map((year) => <option key={year.id} value={year.id}>{year.label}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Guru</span>
            <select value={homeroomDraft.teacherId} onChange={(event) => setHomeroomDraft({ ...homeroomDraft, teacherId: event.target.value })} required>
              <option value="">Pilih guru</option>
              {teachers.map((teacher) => <option key={teacher.id} value={teacher.id}>{teacher.fullName}</option>)}
            </select>
          </label>
          <label className="field">
            <span>Jenjang</span>
            <select value={homeroomDraft.level} onChange={(event) => setHomeroomDraft({ ...homeroomDraft, level: event.target.value })}>
              {levelChoices.map((level) => <option key={level} value={level}>{level}</option>)}
            </select>
          </label>
          <Field label="Kelas" value={homeroomDraft.className} onChange={(value) => setHomeroomDraft({ ...homeroomDraft, className: value })} placeholder="IV A atau KELAS X-IPA" required />
          <button className="button primary" type="submit" disabled={busy || !homeroomYear}>Tetapkan</button>
        </form>
        <div className="homeroom-list">
          {homerooms.map((homeroom) => (
            <div className="homeroom-row" key={homeroom.id}>
              <span><strong>{homeroom.className ?? homeroom.classKey}</strong> · {homeroom.level} · {homeroom.teacher.fullName} · {homeroom.academicYear.label}</span>
              <button className="button secondary small" disabled={busy} onClick={() => void removeHomeroom(homeroom)}>Lepas</button>
            </div>
          ))}
          {homerooms.length === 0 && <span className="result-count">Belum ada wali kelas pada tahun ajaran ini.</span>}
        </div>
      </section>

      {form && (
        <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Data guru">
          <form className="modal-card" onSubmit={(event) => void save(event)}>
            <div className="modal-head">
              <div>
                <p className="eyebrow">{form.id ? 'UBAH DATA' : 'GURU BARU'}</p>
                <h2>{form.id ? 'Ubah data guru' : 'Tambah guru'}</h2>
                <p>Isi identitas resmi dari dokumen kepegawaian. Kosongkan bagian yang belum tersedia.</p>
              </div>
              <button type="button" className="button secondary small" aria-label="Tutup" onClick={() => setForm(null)}><X size={15} /></button>
            </div>

            <div className="section-title">Identitas</div>
            <div className="form-grid">
              <div className="span-2">
                <Field label="Nama lengkap" value={form.fullName} onChange={(value) => setForm({ ...form, fullName: value })} required />
              </div>
              <Field label="NUPTK" value={form.nuptk} onChange={(value) => setForm({ ...form, nuptk: value })} hint="16 digit" />
              <Field label="NIP" value={form.nip} onChange={(value) => setForm({ ...form, nip: value })} />
              <Field label="NIK" value={form.nik} onChange={(value) => setForm({ ...form, nik: value })} hint="16 digit" />
              <ChoiceField label="Jenis kelamin" value={form.gender} onChange={(value) => setForm({ ...form, gender: value })} choices={['MALE', 'FEMALE']} labels={{ MALE: 'Laki-laki', FEMALE: 'Perempuan' }} />
              <Field label="Tempat lahir" value={form.birthPlace} onChange={(value) => setForm({ ...form, birthPlace: value })} />
              <Field label="Tanggal lahir" type="date" value={form.birthDate} onChange={(value) => setForm({ ...form, birthDate: value })} />
              <Field label="Agama" value={form.religion} onChange={(value) => setForm({ ...form, religion: value })} />
            </div>

            <div className="section-title">Kepegawaian</div>
            <div className="form-grid">
              <ChoiceField label="Status kepegawaian" value={form.employmentStatus} onChange={(value) => setForm({ ...form, employmentStatus: value })} choices={employmentChoices} labels={employmentLabels} />
              <Field label="Jabatan / tugas tambahan" value={form.position} onChange={(value) => setForm({ ...form, position: value })} />
              <Field label="Pendidikan terakhir" value={form.highestEducation} onChange={(value) => setForm({ ...form, highestEducation: value })} />
              <Field label="Jurusan / bidang studi" value={form.fieldOfStudy} onChange={(value) => setForm({ ...form, fieldOfStudy: value })} />
              <Field label="Sertifikasi" value={form.certification} onChange={(value) => setForm({ ...form, certification: value })} />
              <Field label="Mulai tugas" type="date" value={form.startedOn} onChange={(value) => setForm({ ...form, startedOn: value })} />
              <div className="span-2">
                <Field label="Mata pelajaran" value={form.subjects} onChange={(value) => setForm({ ...form, subjects: value })} hint="Pisahkan dengan koma, mis. Matematika, IPA" />
              </div>
              <label className="check-field">
                <input type="checkbox" checked={form.isActive} onChange={(event) => setForm({ ...form, isActive: event.target.checked })} />
                Guru masih aktif mengajar
              </label>
            </div>

            <div className="section-title">Kontak dan alamat</div>
            <div className="form-grid">
              <Field label="Email" type="email" value={form.email} onChange={(value) => setForm({ ...form, email: value })} />
              <Field label="No HP" value={form.phone} onChange={(value) => setForm({ ...form, phone: value })} />
              <div className="span-2">
                <Field label="Alamat" value={form.street} onChange={(value) => setForm({ ...form, street: value })} />
              </div>
              <Field label="Kelurahan / desa" value={form.village} onChange={(value) => setForm({ ...form, village: value })} />
              <Field label="Kecamatan" value={form.district} onChange={(value) => setForm({ ...form, district: value })} />
              <Field label="Kabupaten / kota" value={form.regency} onChange={(value) => setForm({ ...form, regency: value })} />
              <Field label="Provinsi" value={form.province} onChange={(value) => setForm({ ...form, province: value })} />
              <div className="span-2">
                <Field label="Catatan" value={form.notes} onChange={(value) => setForm({ ...form, notes: value })} />
              </div>
            </div>

            <div className="modal-actions">
              <button type="button" className="button secondary" onClick={() => setForm(null)} disabled={busy}>Batal</button>
              <button type="submit" className="button primary" disabled={busy}>{form.id ? 'Simpan perubahan' : 'Tambah guru'}</button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}
